import { useState } from 'react';
import { Box, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import useDeleteContent from '@/hooks/content/useDeleteContent';
import {
  getCreatorName,
  getCreatorOrganization,
  getCreatorProfileId,
  getPrimaryRole,
} from '@/modules/content/common/creatorDisplay';
import LoopOverflowMenu from '@/modules/content/loop/parts/LoopOverflowMenu';
import { useAppStore } from '@/store/useAppStore';
import { TContent, TContentReportReason } from '@/types/Content.types';

type Props = {
  content: TContent;
  onNotInterested: () => void;
  onReport: (reason: TContentReportReason) => void;
  isOnDark?: boolean;
};

// Creator identity line: avatar, "Name, Role", organization. Attribution comes straight
// off the card payload, so the loop never fires a per-item profile fetch.
const CreatorIdentity = ({ content, onNotInterested, onReport, isOnDark = false }: Props) => {
  const navigate = useNavigate();
  const userId = useAppStore((state) => state.user?.user_id);
  const profileId = getCreatorProfileId(content);
  const canEdit = userId === content.authorUserId;
  const canDelete = canEdit && !content.isPlatformContent;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const deleteMutation = useDeleteContent();

  const name = getCreatorName(content.creator, content.isPlatformContent);
  const role = getPrimaryRole(content.creator);
  const organization = getCreatorOrganization(content.creator);

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    deleteMutation.reset();
  };

  const confirmDelete = async () => {
    try {
      await deleteMutation.mutateAsync(content.id);
      closeDeleteDialog();
      navigate({ to: '/creator/profile' });
    } catch {
      // Keep the dialog open so the mutation error can be shown and retried.
    }
  };

  return (
    <>
      <HStack width="100%" justify="space-between" align="flex-start" gap="10px">
        <HStack
          gap="10px"
          align="center"
          minWidth={0}
          cursor={profileId ? 'pointer' : 'default'}
          onClick={
            profileId ? () => navigate({ to: '/explore/creator/$userId', params: { userId: profileId } }) : undefined
          }
        >
          <Box
            width="34px"
            height="34px"
            borderRadius="full"
            overflow="hidden"
            flexShrink={0}
            backgroundColor={isOnDark ? 'whiteAlpha.300' : 'background.lightPurple'}
          >
            <Image
              src={content.creator?.photoUrl || '/svg/generic-user.svg'}
              alt=""
              width="100%"
              height="100%"
              objectFit="cover"
            />
          </Box>

          <VStack align="flex-start" gap="1px" minWidth={0}>
            <Text fontSize="13px" fontWeight={700} color={isOnDark ? 'white' : 'text.darkBlue'} noOfLines={1}>
              {role ? `${name}, ${role}` : name}
            </Text>
            {organization && (
              <Text fontSize="12px" color={isOnDark ? 'whiteAlpha.800' : 'text.mediumGray'} noOfLines={1}>
                {organization}
              </Text>
            )}
          </VStack>
        </HStack>

        <Box flexShrink={0}>
          <LoopOverflowMenu
            onNotInterested={onNotInterested}
            onReport={onReport}
            onEdit={
              canEdit
                ? () => navigate({ to: '/creator/content/$contentId', params: { contentId: content.id } })
                : undefined
            }
            onDelete={canDelete ? () => setIsDeleteDialogOpen(true) : undefined}
            canReport={!canEdit}
            isOnDark={isOnDark}
          />
        </Box>
      </HStack>

      <ConfirmationModal
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title={'Delete content?'}
        description={
          'This cannot be undone. The content will be removed from your profile, but MyOmnia will retain a record of it.'
        }
        confirmButtonText={'Delete'}
        confirmButtonColor={'extra.red'}
        confirmLoading={deleteMutation.isPending}
        errorText={deleteMutation.isError ? 'We couldn’t delete this content. Please try again.' : undefined}
      />
    </>
  );
};

export default CreatorIdentity;
